import React, { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { EuiFlexGroup, EuiFlexItem, EuiLoadingSpinner, EuiSpacer, EuiText } from "@elastic/eui";
import { shallowEqual, useSelector } from "react-redux";
import { saveAs } from "file-saver";
import AlertsService from "services/AdminConsole/Monitoring/Profiles/MonitoringProfilesService";
import { useFetch } from "hooks/useFetch";
import AddButton from "components/Buttons/AddButton";
import VerticalRule from "components/VerticalRule";
import TopFilter from "components/TopFilter/TopFilter";
import { ProfileIcon } from "components/Icons";
import ConfirmModal from "components/Modal/ConfirmModal";
import TablePanel from "components/Panels/TablePanel";
import Badge from "components/Badge/Badge";
import ActionButtons from "components/Buttons/ActionButtons";
import { badgeColors, badgeTextColors, NEGATE_FILTER } from "Consts";
import Accordion from "components/Accordion/Accordion";
import { globalFilterSelector } from "reduxStore/globalFilter/globalFilter.selectors";
import { authSelector } from "reduxStore/auth/auth.selectors";
import NewProfile from "./components/NewProfile";
import { GetProfileModel, Profile as ProfileType } from "./profileTypes";
import Profile from "./components/Profile";
import EditProfile from "./components/EditProfile";
import CopyProfile from "./components/CopyProfile";
import ProfileAssignmentIcons from "./components/ProfileAssignmentIcons";
import { filters, SEVERITY_COLORS } from "./profileConsts";
import { getProfileExportConfig, isProfileIncomplete } from "./profileUtils";

const Profiles = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [isCopyModalOpen, setIsCopyModalOpen] = useState(false);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [profileToDelete, setProfileToDelete] = useState<ProfileType | null>(null);
  const [searchQuery, setSearchQuery] = useState("");
  const [expandedIds, setExpandedIds] = useState<number[]>([]);
  const [selectedProfile, setSelectedProfile] = useState<ProfileType | null>(null);
  const [profileToCopy, setProfileToCopy] = useState<ProfileType | null>(null);
  const [filteredProfiles, setFilteredProfiles] = useState<ProfileType[] | null>(null);
  const { clientId, GlobalTopFilterObject, GlobalTopFilterObjectValues, ExceptionTopFilterObject } =
    useSelector(globalFilterSelector, shallowEqual);
  const { currentUser } = useSelector(authSelector, shallowEqual);
  const { data, isLoading, refetch } = useFetch<{ clientId: string }, GetProfileModel>({
    serviceFn: AlertsService.getProfiles,
    errorMessage: "Failed to fetch profiles",
    deps: [ExceptionTopFilterObject],
  });
  const { profiles } = data || {};

  const onToggle = (id: number, isExpanded: boolean) => {
    if (isExpanded) {
      setExpandedIds([id]);
    } else {
      setExpandedIds([]);
    }
  };

  const handleEdit = (e: React.MouseEvent<HTMLButtonElement, MouseEvent>, profile: ProfileType) => {
    e.stopPropagation();
    setSelectedProfile(profile);
    setIsEditModalOpen(true);
  };

  const handleCopy = (e: React.MouseEvent<HTMLButtonElement, MouseEvent>, profile: ProfileType) => {
    e.stopPropagation();
    setProfileToCopy(profile);
    setIsCopyModalOpen(true);
  };

  const handleConfirmDeleteProfile = async () => {
    setIsDeleting(true);
    try {
      const result = await AlertsService.deleteProfile({
        clientId,
        monitoringProfileId: profileToDelete?.id,
      });
      if (result) {
        refetch();
        setExpandedIds([]);
      }
    } catch (error) {
    } finally {
      setIsDeleting(false);
      setIsDeleteModalOpen(false);
    }
  };

  const filterProfiles = () => {
    return (
      profiles?.filter((profile) => {
        const nameMatch = profile.name.toLowerCase().includes(searchQuery.toLowerCase());

        const selectedResourceIds = [
          ...(GlobalTopFilterObjectValues?.hosts?.length > 0
            ? GlobalTopFilterObjectValues?.hosts
            : []),
          ...(GlobalTopFilterObjectValues.servername?.length > 0
            ? GlobalTopFilterObjectValues.servername
            : []),
        ];

        const excludeResources =
          selectedResourceIds
            ?.filter((s: any) => s.startsWith(NEGATE_FILTER))
            ?.map((s: any) => s.replace(NEGATE_FILTER, "")) || [];

        const includeResources =
          selectedResourceIds?.filter((s: any) => !s.startsWith(NEGATE_FILTER)) || [];

        const instanceMatch =
          !GlobalTopFilterObject.servername?.length ||
          (!profile.resourceAssignments.some((i: any) =>
            excludeResources.some((resourceId: string) => `${i?.resourceId}` === resourceId)
          ) &&
            (includeResources.length === 0 ||
              profile.resourceAssignments.some((i: any) =>
                includeResources.some((resourceId: string) => `${i?.resourceId}` === resourceId)
              )));

        const excludeAlertTypes =
          GlobalTopFilterObject.alertTypeId
            ?.filter((t: any) => t.startsWith(NEGATE_FILTER))
            .map((t: any) => t.replace(NEGATE_FILTER, "")) || [];
        const includeAlertTypes =
          GlobalTopFilterObject.alertTypeId?.filter((t: any) => !t.startsWith(NEGATE_FILTER)) || [];

        const alertTypeMatch =
          !GlobalTopFilterObject.alertTypeId?.length ||
          (!profile.alertTypeAssignments.some((alertType) =>
            excludeAlertTypes.includes(alertType.name)
          ) &&
            (includeAlertTypes.length === 0 ||
              profile.alertTypeAssignments.some((alertType) =>
                includeAlertTypes.includes(alertType.name)
              )));

        return nameMatch && instanceMatch && alertTypeMatch;
      }) || []
    );
  };

  useEffect(() => {
    setFilteredProfiles(filterProfiles());
  }, [searchQuery, GlobalTopFilterObject, profiles]);

  const exportAllAsJsonAction = {
    key: "exportAllJson",
    name: "Export All (JSON)",
    icon: "download",
    onClick: () => {
      if (!filteredProfiles || filteredProfiles.length === 0) {
        return;
      }
      const baseFileName = "monitoring_profiles";
      const jsonFileName = `${baseFileName}.json`;
      const jsonData = JSON.stringify(filteredProfiles, null, 2);
      const blob = new Blob([jsonData], { type: "application/json;charset=utf-8" });
      saveAs(blob, jsonFileName);
    },
    buttonProps: {
      disabled: !filteredProfiles || filteredProfiles.length === 0,
    },
  };

  useEffect(() => {
    if (!location?.search?.includes("add")) return;
    setIsCreateModalOpen(true);
    navigate(location.pathname);
  }, [location?.search]);

  return (
    <div>
      <TopFilter Filters={filters} isFixed />

      <TablePanel
        data={[1]}
        columns={[]}
        onSearch={setSearchQuery}
        panelTitleProps={{ title: "Profiles" }}
        rightButtons={[<AddButton onClick={() => setIsCreateModalOpen(true)} text="Profile" />]}
        showExportButton={false}
        titleActions={[exportAllAsJsonAction]}
        loading={false}>
        {isLoading ? (
          <EuiFlexGroup justifyContent="center" alignItems="center">
            <EuiFlexItem grow={false}>
              <EuiSpacer size="xxl" />
              <EuiLoadingSpinner size="l" />
            </EuiFlexItem>
          </EuiFlexGroup>
        ) : !filteredProfiles || filteredProfiles?.length === 0 ? (
          <EuiFlexGroup justifyContent="center">
            <EuiFlexItem grow={false}>
              <p>No profiles found</p>
            </EuiFlexItem>
          </EuiFlexGroup>
        ) : (
          filteredProfiles?.map((profile) => {
            const canDelete = profile.canDelete && profile.resourceAssignments?.length === 0;

            const renderLinkToLists = () => (
              <>
                {profile?.isSyncedToClients && (
                  <EuiFlexItem grow={false} style={{ marginRight: "20px" }}>
                    <Badge
                      iconType="sortable"
                      text="Synced"
                      style={{
                        color: "var(--green)",
                        backgroundColor: "transparent",
                        borderColor: "var(--green)",
                      }}
                    />
                  </EuiFlexItem>
                )}

                <EuiFlexItem grow={false} style={{ marginRight: "10px" }}>
                  <ProfileAssignmentIcons profile={profile} />
                </EuiFlexItem>

                <VerticalRule />
                {(profile?.canEdit || profile?.isSystemProfile) && (
                  <ActionButtons
                    length={filteredProfiles?.some((p) => p.canDelete) ? 4 : 3}
                    currentUser={currentUser}
                    buttons={[
                      ...(canDelete
                        ? [
                            {
                              type: "trash",
                              disabled: isLoading,
                              tooltip: "Delete Profile",
                              onClick: (e: any) => {
                                e.stopPropagation();
                                setProfileToDelete(profile);
                                setIsDeleteModalOpen(true);
                              },
                            },
                          ]
                        : []),
                      {
                        type: "copy",
                        disabled: isLoading,
                        tooltip: "Copy Profile",
                        onClick: (e) => {
                          handleCopy(e, profile);
                        },
                      },
                      {
                        type: "export",
                        disabled: isLoading,
                        tooltip: "Export Profile",
                        exportConfig: getProfileExportConfig(profile),
                        onClick: () => {},
                      },
                      {
                        type: "pencil",
                        disabled: isLoading,
                        tooltip: "Edit Profile",
                        onClick: (e) => {
                          handleEdit(e, profile);
                        },
                      },
                    ]}
                  />
                )}
                {!(profile?.canEdit || profile?.isSystemProfile) && (
                  <ActionButtons
                    length={2}
                    currentUser={currentUser}
                    buttons={[
                      {
                        type: "export",
                        disabled: true,
                        exportConfig: getProfileExportConfig(profile),
                        onClick: () => {},
                      },
                      {
                        type: "info",
                        popoverMessage: profile?.readOnlyReason,
                        onClick: () => {},
                        color: SEVERITY_COLORS.Warning,
                      },
                    ]}
                  />
                )}
              </>
            );

            return (
              <React.Fragment key={profile.id}>
                <Accordion
                  loading={isLoading}
                  type={profile.id}
                  forceState={expandedIds.includes(profile.id) ? "open" : "closed"}
                  onToggle={(isExpanded: any) => onToggle(profile.id, isExpanded)}
                  buttonContent={
                    <EuiFlexGroup alignItems="center" justifyContent="spaceBetween" gutterSize="s">
                      <EuiFlexItem grow={false}>
                        <EuiFlexGroup alignItems="center" gutterSize="s">
                          <EuiFlexItem grow={false}>
                            <ProfileIcon />
                          </EuiFlexItem>
                          <EuiFlexGroup gutterSize="s" alignItems="center">
                            <EuiFlexItem grow={false}>
                              <EuiText size="s" style={{ fontWeight: 500 }}>
                                {profile.name}
                              </EuiText>
                            </EuiFlexItem>
                            <EuiFlexItem grow={false}>
                              {isProfileIncomplete(profile) ? (
                                <Badge
                                  text={"Incomplete"}
                                  color={badgeColors.$red}
                                  style={{
                                    color: badgeTextColors.$red,
                                    border: `1px solid ${badgeTextColors.$red}`,
                                    backgroundColor: "transparent",
                                  }}
                                />
                              ) : (
                                <Badge
                                  text={"Operational"}
                                  color={badgeColors.$green}
                                  style={{
                                    color: badgeTextColors.$green,
                                    border: `1px solid ${badgeTextColors.$green}`,
                                    backgroundColor: "transparent",
                                  }}
                                />
                              )}
                            </EuiFlexItem>
                          </EuiFlexGroup>
                        </EuiFlexGroup>
                      </EuiFlexItem>
                      <EuiFlexItem grow={false}>
                        <EuiFlexGroup alignItems="center" gutterSize="s">
                          {renderLinkToLists()}
                        </EuiFlexGroup>
                      </EuiFlexItem>
                    </EuiFlexGroup>
                  }
                  children={
                    <>
                      {expandedIds.includes(profile.id) && (
                        <Profile profile={profile} showAsLabeledRow={true} />
                      )}
                    </>
                  }
                />
                <EuiSpacer size="s" />
              </React.Fragment>
            );
          })
        )}
        {isCreateModalOpen && (
          <NewProfile
            setIsModalOpen={setIsCreateModalOpen}
            profiles={profiles || []}
            refetch={refetch}
            setExpandedIds={setExpandedIds}
          />
        )}
        {isEditModalOpen && selectedProfile !== null && (
          <EditProfile
            setIsModalOpen={setIsEditModalOpen}
            profiles={profiles || []}
            currentProfile={selectedProfile}
            refetch={refetch}
            setExpandedIds={setExpandedIds}
          />
        )}
        {isCopyModalOpen && profileToCopy !== null && (
          <CopyProfile
            setIsModalOpen={setIsCopyModalOpen}
            profileToCopy={profileToCopy}
            profiles={profiles ?? []}
            refetch={refetch}
          />
        )}
        {isDeleteModalOpen && (
          <ConfirmModal
            cancelButtonText="Cancel"
            confirmButtonText="Delete"
            onClose={() => {
              setIsDeleteModalOpen(false);
            }}
            onConfirm={handleConfirmDeleteProfile}
            isLoading={isDeleting}
            title="Delete Profile"
            message={
              <p>
                You&rsquo;re about to delete <b>{profileToDelete?.name || ""}</b>. <br></br>
                This action cannot be undone.
              </p>
            }
          />
        )}
      </TablePanel>
    </div>
  );
};

export default Profiles;
